import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIResponse } from "@/types/custom-http";
import { Workspace, WorkspaceInfo, WorkspaceState } from "./workspaces.interface";
import * as thunks from "./workspacesThunks";

const initialState: WorkspaceState = {
  items: [],
  info: [],
  selectedWorkspace: null,
  loading: false,
  error: null,
};

const workspacesSlice = createSlice({
  name: "workspaces",
  initialState,
  reducers: {
    clearWorkspaces: (state) => {
      state.items = [];
    },
    clearWorkspaceInfo: (state) => {
      state.info = [];
    },
    clearSelectedWorkspace: (state) => {
      state.selectedWorkspace = null;
    },
    clearWorkspaceError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Listado
      .addCase(thunks.fetchWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchWorkspaces.fulfilled, (state, action: PayloadAction<APIResponse<Workspace[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items = action.payload.data;
        }
      })
      .addCase(thunks.fetchWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Info
      .addCase(thunks.fetchWorkspacesInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchWorkspacesInfo.fulfilled, (state, action: PayloadAction<APIResponse<WorkspaceInfo[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.info = action.payload.data;
        }
      })
      .addCase(thunks.fetchWorkspacesInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // By Id
      .addCase(thunks.fetchWorkspaceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchWorkspaceById.fulfilled, (state, action: PayloadAction<APIResponse<Workspace>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.selectedWorkspace = action.payload.data;
        }
      })
      .addCase(thunks.fetchWorkspaceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Crear
      .addCase(thunks.createWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createWorkspace.fulfilled, (state, action: PayloadAction<APIResponse<Workspace>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items.push(action.payload.data);
        }
      })
      .addCase(thunks.createWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Actualizar
      .addCase(thunks.updateWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateWorkspace.fulfilled, (state, action: PayloadAction<APIResponse<Workspace>>) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedWorkspace && state.selectedWorkspace.id === action.payload.data?.id) {
          state.selectedWorkspace = action.payload.data;
        }
      })
      .addCase(thunks.updateWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      });
  },
});

export const selectAllWorkspaces = (state: RootState) => state.workspaces.items;
export const selectWorkspaceInfo = (state: RootState) => state.workspaces.info;
export const selectWorkspaceById = (state: RootState, id: number) => state.workspaces.items.find((item) => item.id === id);
export const selectSelectedWorkspace = (state: RootState) => state.workspaces.selectedWorkspace;
export const selectWorkspacesLoading = (state: RootState) => state.workspaces.loading;
export const selectWorkspacesError = (state: RootState) => state.workspaces.error;

export const {
  clearWorkspaces,
  clearWorkspaceInfo,
  clearSelectedWorkspace,
  clearWorkspaceError,
} = workspacesSlice.actions;

export default workspacesSlice.reducer;
